import {clsx} from 'clsx'
import {Tooltip} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {memo, use} from 'react'
import type {AnchorHTMLAttributes, ComponentProps, FocusEvent, MouseEvent, ReactNode, Ref} from 'react'
import {LayoutLink as Link} from '../layout-runtime'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {GroupedContext} from './sidebar-context'
import {getCurrentState, sidebarChildKind} from './contains-current'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import styles from './Item.module.css'
import rowStyles from './Row.module.css'

type SidebarItemLinkProps =
  | {href: string; to?: never; forceHardNavigation?: never}
  | {to: string; href?: never; forceHardNavigation?: boolean}

export type ItemProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> &
  SidebarItemLinkProps & {
    children: ReactNode
    preload?: ComponentProps<typeof Link>['preload']
    state?: ComponentProps<typeof Link>['state']
    leadingVisual?: ReactNode
    trailingVisual?: ReactNode
    trailingAction?: ReactNode
    actionVisibility?: 'always' | 'hover' | 'muted'
    tooltipText?: string
    inactiveText?: string
    current?: boolean
    ref?: Ref<HTMLAnchorElement | HTMLButtonElement>
  }

type ConsumerHandlers = Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'onFocus' | 'onMouseEnter' | 'onMouseOverCapture'>

// Keep consumer handlers out of Tooltip's cloning and delayed event forwarding.
function ItemLink({
  children,
  consumerHandlers,
  expandedDescription,
  onFocus,
  onMouseOverCapture,
  onMouseEnter: _tooltipMouseEnter,
  to,
  forceHardNavigation,
  href,
  preload,
  state,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  consumerHandlers: ConsumerHandlers
  expandedDescription?: string
  to?: string
  forceHardNavigation?: boolean
  ref?: Ref<HTMLAnchorElement>
  preload?: ComponentProps<typeof Link>['preload']
  state?: ComponentProps<typeof Link>['state']
}) {
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const anchorProps = {
    ...props,
    'aria-describedby': visualIsExpanded ? expandedDescription : props['aria-describedby'],
    ...(!visualIsExpanded || consumerHandlers.onFocus
      ? {
          onFocus: (event: FocusEvent<HTMLAnchorElement>) => {
            consumerHandlers.onFocus?.(event)
            onFocus?.(event)
          },
        }
      : {}),
    ...(consumerHandlers.onMouseEnter ? {onMouseEnter: consumerHandlers.onMouseEnter} : {}),
    onMouseOverCapture: (event: MouseEvent<HTMLAnchorElement>) => {
      consumerHandlers.onMouseOverCapture?.(event)
      onMouseOverCapture?.(event)
    },
  }
  return to && !forceHardNavigation ? (
    <Link {...anchorProps} to={to} preload={preload} state={state}>
      {children}
    </Link>
  ) : (
    <a {...anchorProps} href={href ?? to}>
      {children}
    </a>
  )
}

export const Item = Object.assign(
  memo(function SidebarItem({
    children,
    href,
    to,
    forceHardNavigation,
    leadingVisual,
    trailingVisual,
    trailingAction,
    actionVisibility = 'always',
    tooltipText,
    inactiveText,
    current,
    'aria-current': ariaCurrent,
    ref,
    className,
    onFocus,
    onMouseEnter,
    onMouseOverCapture,
    ...linkProps
  }: ItemProps) {
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const grouped = use(GroupedContext)
    const currentState = getCurrentState({current, inactiveText, 'aria-current': ariaCurrent})
    if (!grouped && !leadingVisual) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Item] Items outside a SectionGroup or Accordion need leadingVisual to appear in the rail. This item is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    const content = (
      <>
        {leadingVisual ? (
          <span className={clsx(styles.LeadingVisual, !grouped && rowStyles.Icon)}>{leadingVisual}</span>
        ) : null}
        <span
          data-sidebar-collapse-content
          className={clsx(rowStyles.Label, !visualIsExpanded && styles.visuallyHidden)}
        >
          {children}
        </span>
        {visualIsExpanded && trailingVisual ? (
          <span data-sidebar-collapse-content className={styles.TrailingVisual}>
            {trailingVisual}
          </span>
        ) : null}
      </>
    )
    const setItemRef = (element: HTMLAnchorElement | HTMLButtonElement | null) => {
      if (typeof ref === 'function') return ref(element)
      if (ref) ref.current = element
    }
    const anchorProps = {
      ...linkProps,
      ref: setItemRef,
      'aria-current': currentState.ariaCurrent,
      className: clsx(
        rowStyles.Row,
        styles.Link,
        currentState.current && rowStyles.current,
        grouped && !leadingVisual && rowStyles.textLed,
        inactiveText && styles.inactive,
        className,
      ),
    }
    const link = inactiveText ? (
      <button
        type="button"
        ref={setItemRef}
        className={anchorProps.className}
        aria-label={linkProps['aria-label']}
        aria-labelledby={linkProps['aria-labelledby']}
        aria-disabled="true"
      >
        {content}
      </button>
    ) : (
      <ItemLink
        {...anchorProps}
        href={href}
        to={to}
        forceHardNavigation={forceHardNavigation}
        consumerHandlers={{onFocus, onMouseEnter, onMouseOverCapture}}
        expandedDescription={linkProps['aria-describedby']}
      >
        {content}
      </ItemLink>
    )
    const tooltipLabel = tooltipText ?? (typeof children === 'string' ? children : '')
    const tooltipContent = inactiveText
      ? [!visualIsExpanded && tooltipLabel, inactiveText].filter(Boolean).join(': ')
      : visualIsExpanded
        ? ''
        : tooltipLabel
    return (
      <div
        className={clsx(
          styles.Item,
          actionStyles.actionRow,
          currentState.current && styles.current,
          inactiveText && styles.inactive,
        )}
        data-component="Sidebar.Item"
      >
        {inactiveText || tooltipText || typeof children === 'string' ? (
          <FeatureFlags flags={{primer_react_merged_forwarded_refs: true}}>
            <Tooltip
              type="description"
              text={tooltipContent}
              direction="e"
              delay="medium"
              _privateDisableTooltip={visualIsExpanded && !inactiveText}
              popover={visualIsExpanded && !inactiveText ? undefined : 'auto'}
            >
              {link}
            </Tooltip>
          </FeatureFlags>
        ) : (
          link
        )}
        {visualIsExpanded && trailingAction ? (
          <SidebarAction hover={actionVisibility === 'hover'} muted={actionVisibility === 'muted'}>
            {trailingAction}
          </SidebarAction>
        ) : null}
      </div>
    )
  }),
  {[sidebarChildKind]: 'item' as const},
)
